import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { cn } from '@/lib/utils';

export type QuoteScope = 'own' | 'group';

/**
 * Périmètre de la cotation d'un corporate : « Ma société » ou « Groupe » (ses filiales
 * comprises), et, une fois le comparatif rendu, les sociétés dont on veut voir les offres.
 * Le périmètre est ce que le serveur calcule ; les sociétés cochées ne font que filtrer l'affichage.
 */
export function GroupScopeBar({
  scope,
  onScope,
  groupSize,
  owners,
  ownCompanyId,
  hidden,
  onToggleOwner,
}: {
  scope: QuoteScope;
  onScope: (scope: QuoteScope) => void;
  /** Société qui cote + ses filiales. */
  groupSize: number;
  /** Sociétés dont la dernière cotation a lu les grilles, la nôtre d'abord. */
  owners: Array<{ companyId: string; name: string }>;
  ownCompanyId: string | null;
  hidden: Record<string, boolean>;
  onToggleOwner: (companyId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 space-y-2 rounded border border-border/60 bg-surface px-4 py-2.5 shadow-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-2xs font-semibold uppercase tracking-wider text-subtle">{t('group.quote.scopeLabel')}</span>
        <SegmentedControl
          aria-label={t('group.quote.scopeLabel')}
          value={scope}
          onChange={(v) => onScope(v as QuoteScope)}
          options={[
            { value: 'own', label: t('group.quote.own') },
            { value: 'group', label: t('group.quote.group', { count: groupSize }) },
          ]}
        />
      </div>
      {scope === 'group' && owners.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-2xs font-semibold uppercase tracking-wider text-subtle">{t('group.quote.sources')}</span>
          {owners.map((o) => (
            <button
              key={o.companyId}
              type="button"
              aria-pressed={!hidden[o.companyId]}
              onClick={() => onToggleOwner(o.companyId)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                !hidden[o.companyId]
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border bg-surface text-muted',
              )}
            >
              {o.companyId === ownCompanyId ? t('group.own') : o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
